import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import RichTextEditor from '../ui/RichTextEditor';
import { 
  ArrowLeft, 
  Save, 
  Calendar, 
  DollarSign, 
  Award,
  AlertCircle,
  CheckCircle,
  FileText,
  Users,
  Globe,
  Upload,
  Image as ImageIcon,
  MapPin,
  ChevronDown,
  ChevronUp,
  Building,
  Grid3X3,
  RefreshCw,
  Settings
} from 'lucide-react';
import '../../styles/CollectionDetailAdmin.css';
import '../../styles/NewSubmission.css';

const NewSubmission = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get URL parameters
  const collectionIdParam = searchParams.get('collection_id');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userGalleries, setUserGalleries] = useState([]);
  const [prefilledCollection, setPrefilledCollection] = useState(null);
  const [prefilledGallery, setPrefilledGallery] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);
  const [organizationTemplate, setOrganizationTemplate] = useState(null);
  const [formSections, setFormSections] = useState([]);
  const [saveTimeout, setSaveTimeout] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    collection_id: collectionIdParam || '',
    gallery_id: '',
    title: '',
    callout_headline: '',
    theme: '',
    cover_image: '',
    open_date: '',
    submission_deadline: '',
    max_allowed: 3,
    submission_price: '25.00',
    featured: false,
    member_free: false,
    has_video: false,
    external_url: '',
    mandrill_template: '',
    form_config: JSON.stringify([], null, 2)
  });
  
  // Lookup data
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);

  useEffect(() => {
    initializeComponent();
  }, []);

  useEffect(() => {
    // When collection_id parameter is provided, load and prefill it
    if (collectionIdParam) {
      loadAndPrefillCollection(collectionIdParam);
    }
  }, [collectionIdParam]);

  useEffect(() => {
    // Auto-size JSON textareas
    const jsonTextareas = document.querySelectorAll('.json-input');
    jsonTextareas.forEach(textarea => {
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(textarea.scrollHeight, 120) + 'px';
    });
  }, [formData.acceptance_info, formData.form_config]);

  const initializeComponent = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }
      setCurrentUser(user);

      // Get user's gallery access
      const { data: galleryAccess, error: accessError } = await supabase
        .from('gallery_access')
        .select('gallery_id')
        .eq('user_uuid', user.id);

      if (accessError) throw accessError;

      if (!galleryAccess || galleryAccess.length === 0) {
        setError('You do not have access to any galleries. Please contact an administrator.');
        return;
      }

      const galleryIds = galleryAccess.map(access => access.gallery_id);

      // Load galleries the user has access to
      const { data: galleries, error: galleriesError } = await supabase
        .from('galleries')
        .select('gallery_id, name, address')
        .in('gallery_id', galleryIds)
        .order('name');

      if (galleriesError) throw galleriesError;
      setUserGalleries(galleries || []);

      // If no collection_id param, load all collections
      if (!collectionIdParam) {
        await loadCollectionsForGalleries(galleryIds);
      }
    } catch (error) {
      console.error('Error initializing component:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAndPrefillCollection = async (collectionId) => {
    try {
      // Load the collection data with organization
      const { data: collection, error: collError } = await supabase
        .from('collections')
        .select('collection_id, title, cover_image, organization_id, organizations(organization_id, name)')
        .eq('collection_id', collectionId)
        .single();

      if (collError) throw collError;
      setPrefilledCollection(collection);
      
      // Set organization if available from collection
      if (collection.organizations) {
        setOrganization(collection.organizations);
        
        // Load organization template if available
        const { data: templateData } = await supabase
          .from('organization_submission_templates')
          .select('*')
          .eq('organization_id', collection.organizations.organization_id)
          .eq('is_default', true)
          .single();
        
        if (templateData) {
          setOrganizationTemplate(templateData);
          
          // Initialize form sections from template
          const templateFields = typeof templateData.field_definitions === 'string'
            ? JSON.parse(templateData.field_definitions)
            : templateData.field_definitions;
          
          if (Array.isArray(templateFields)) {
            setFormSections(templateFields);
            setFormData(prev => ({
              ...prev,
              form_config: JSON.stringify(templateFields, null, 2)
            }));
          }
        }
      }

      // Get the gallery this collection belongs to
      const { data: mapping, error: mapError } = await supabase
        .from('gallery_collection_mapping')
        .select('gallery_id')
        .eq('collection_id', collectionId)
        .single();

      if (mapError) throw mapError;

      // Load the gallery details
      const { data: gallery, error: galError } = await supabase
        .from('galleries')
        .select('gallery_id, name, address')
        .eq('gallery_id', mapping.gallery_id)
        .single();

      if (galError) throw galError;
      setPrefilledGallery(gallery);

      // Prefill the form
      setFormData(prev => ({
        ...prev,
        collection_id: collectionId,
        gallery_id: mapping.gallery_id,
        cover_image: collection.cover_image || prev.cover_image
      }));
    } catch (error) {
      console.error('Error loading prefilled collection:', error);
      setError(`Failed to load collection: ${error.message}`);
    }
  };

  const loadCollectionsForGalleries = async (galleryIds) => {
    try {
      const { data: mappings, error: mapError } = await supabase
        .from('gallery_collection_mapping')
        .select('collection_id')
        .in('gallery_id', galleryIds);

      if (mapError) throw mapError;

      const collectionIds = mappings?.map(m => m.collection_id) || [];

      if (collectionIds.length > 0) {
        const { data: collections, error: collError } = await supabase
          .from('collections')
          .select('collection_id, title, cover_image')
          .in('collection_id', collectionIds)
          .order('title');

        if (collError) throw collError;
        setCollections(collections || []);
      }
    } catch (error) {
      console.error('Error loading collections:', error);
    }
  };

  // Load collections for a specific gallery
  const loadCollectionsForGallery = async (galleryId) => {
    try {
      const { data: mappings, error: mapError } = await supabase
        .from('gallery_collection_mapping')
        .select('collection_id')
        .eq('gallery_id', galleryId);

      if (mapError) throw mapError;

      const collectionIds = mappings?.map(m => m.collection_id) || [];

      if (collectionIds.length > 0) {
        const { data: collectionsData, error: collError } = await supabase
          .from('collections')
          .select('collection_id, title, cover_image')
          .in('collection_id', collectionIds)
          .order('title');

        if (collError) throw collError;
        setCollections(collectionsData || []);
      } else {
        setCollections([]);
      }
    } catch (error) {
      console.error('Error loading collections for gallery:', error);
      setCollections([]);
    }
  };

  useEffect(() => {
    if (formData.collection_id) {
      loadCollectionData();
    }
  }, [formData.collection_id]);

  // When gallery_id changes, load collections for that gallery
  useEffect(() => {
    if (formData.gallery_id && !prefilledCollection) {
      loadCollectionsForGallery(formData.gallery_id);
      // Clear collection selection when gallery changes
      setFormData(prev => ({
        ...prev,
        collection_id: ''
      }));
    }
  }, [formData.gallery_id]);

  const loadCollectionData = async () => {
    try {
      const { data, error } = await supabase
        .from('collections')
        .select('*, organizations(organization_id, name)')
        .eq('collection_id', formData.collection_id)
        .single();
      
      if (error) throw error;
      setSelectedCollection(data);
      
      // Auto-fill cover image from collection if not already set
      if (data.cover_image && !formData.cover_image) {
        setFormData(prev => ({
          ...prev,
          cover_image: data.cover_image
        }));
      }
      
      // Set organization and load template
      if (data.organizations) {
        setOrganization(data.organizations);
        
        // Load organization template if available
        const { data: templateData } = await supabase
          .from('organization_submission_templates')
          .select('*')
          .eq('organization_id', data.organizations.organization_id)
          .eq('is_default', true)
          .single();
        
        if (templateData) {
          setOrganizationTemplate(templateData);
          
          // Initialize form sections from template only if formSections is empty
          if (formSections.length === 0) {
            const templateFields = typeof templateData.field_definitions === 'string'
              ? JSON.parse(templateData.field_definitions)
              : templateData.field_definitions;
            
            if (Array.isArray(templateFields)) {
              setFormSections(templateFields);
              setFormData(prev => ({
                ...prev,
                form_config: JSON.stringify(templateFields, null, 2)
              }));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading collection data:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle form section management
  const updateFormSection = (index, field, value) => {
    if (!Array.isArray(formSections)) return;
    const updatedSections = formSections.map((section, i) => 
      i === index ? { ...section, [field]: value } : section
    );
    setFormSections(updatedSections);
    updateFormConfig(updatedSections);
  };

  const updateFormConfig = (sections) => {
    // Debounce to prevent infinite loops
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    const timeout = setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        form_config: JSON.stringify(sections, null, 2)
      }));
    }, 300);
    
    setSaveTimeout(timeout);
  };

  const syncWithTemplate = async () => {
    if (!organization?.organization_id) {
      alert('No organization linked to this submission');
      return;
    }

    try {
      // Fetch the LATEST template from database
      const { data: latestTemplate, error: fetchError } = await supabase
        .from('organization_submission_templates')
        .select('*')
        .eq('organization_id', organization.organization_id)
        .eq('is_default', true)
        .single();

      if (fetchError || !latestTemplate?.field_definitions) {
        alert('No template found for this organization');
        return;
      }

      // Update the cached template state with fresh data
      setOrganizationTemplate(latestTemplate);

      const templateFields = typeof latestTemplate.field_definitions === 'string'
        ? JSON.parse(latestTemplate.field_definitions)
        : latestTemplate.field_definitions;

      if (!Array.isArray(templateFields)) {
        alert('Template has invalid field definitions');
        return;
      }

      setFormSections(templateFields);
      setFormData(prev => ({
        ...prev,
        form_config: JSON.stringify(templateFields, null, 2)
      }));

      alert('Form synced with latest template!');
    } catch (err) {
      console.error('Error syncing with template:', err);
      alert('Error syncing with template: ' + err.message);
    }
  };

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const result = await uploadToCloudinary(file, 'image', 'submission_covers');
      setCoverImageFile(file);
      handleInputChange('cover_image', result.url);
    } catch (error) {
      console.error('Error uploading cover image:', error);
      setError('Failed to upload cover image');
    } finally {
      setUploadingImage(false);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      // Parse the stored UTC/ISO date string and convert to local time for the input
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      console.warn('Invalid date format:', dateString);
      return '';
    }
  };

  const handleDateChange = (field, value) => {
    if (!value) {
      handleInputChange(field, '');
      return;
    }

    try {
      // datetime-local value is in the user's local timezone (e.g. "2026-04-11T23:59")
      const localDate = new Date(value);
      if (isNaN(localDate.getTime())) {
        console.warn('Invalid date:', value);
        return;
      }

      // Convert local time to UTC ISO string for DB storage
      handleInputChange(field, localDate.toISOString());
    } catch (error) {
      console.error('Error processing date:', error);
    }
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.title.trim()) errors.push('Title is required');
    if (!formData.collection_id) errors.push('Collection is required');
    if (!formData.gallery_id) errors.push('Gallery is required');
    if (!formData.submission_deadline) errors.push('Submission deadline is required');
    
    // Validate form_config JSON if provided (acceptance_info is optional legacy field)
    if (formData.form_config && formData.form_config.trim() !== '') {
      try {
        JSON.parse(formData.form_config);
      } catch (e) {
        errors.push('Form config must be valid JSON');
      }
    }

    return errors;
  };

  // Helper to safely parse JSON with fallback
  const safeParseJSON = (jsonString, fallback = {}) => {
    if (!jsonString || jsonString.trim() === '') return fallback;
    try {
      return JSON.parse(jsonString);
    } catch {
      return fallback;
    }
  };

  const createStripeProduct = async (submissionData) => {
    if (parseFloat(submissionData.submission_price) === 0) {
      return { product_id: null, price_id: null };
    }

    try {
      const response = await supabase.functions.invoke('stripe_create_submission_product', {
        body: {
          title: submissionData.title,
          description: submissionData.title,
          price: parseFloat(submissionData.submission_price),
          image_url: submissionData.cover_image || 'https://via.placeholder.com/400x300',
          collection_id: submissionData.collection_id
        }
      });

      if (response.error) throw response.error;
      return response.data;
    } catch (error) {
      console.error('Error creating Stripe product:', error);
      throw new Error('Failed to create payment product');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Create Stripe product if there's a price
      let stripeData = { product_id: null, price_id: null };
      if (parseFloat(formData.submission_price) > 0) {
        stripeData = await createStripeProduct(formData);
      }

      // Prepare submission data
      const submissionData = {
        ...formData,
        submission_price: parseFloat(formData.submission_price),
        max_allowed: parseInt(formData.max_allowed),
        stripe_product_id: stripeData.product_id,
        stripe_plan_id: stripeData.price_id,
        acceptance_info: safeParseJSON(formData.acceptance_info, {}),
        form_config: safeParseJSON(formData.form_config, []),
        order: 0 // Default order
      };

      // Insert submission
      const { data, error } = await supabase
        .from('submissions_details')
        .insert([submissionData])
        .select()
        .single();

      if (error) throw error;

      setSuccess(true);
      
      // Redirect after a brief delay - Go back to collection detail admin
      setTimeout(() => {
        navigate(`/curator/collections/${formData.collection_id}?tab=submissions`);
      }, 2000);

    } catch (error) {
      console.error('Error creating submission:', error);
      setError(error.message || 'Failed to create submission');
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="edit-submission-container">
        <div className="success-message">
          <CheckCircle size={48} />
          <h2>Submission Created Successfully!</h2>
          <p>Redirecting to submissions list...</p>
        </div>
      </div>
    );
  }

  if (loading && !currentUser) {
    return (
      <div className="edit-submission-container">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem 2rem', minHeight: '60vh' }}>
          <div className="spinner" style={{ width: '40px', height: '40px', marginBottom: '1rem' }}></div>
          <p>Loading your galleries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-submission-container">
      <div className="submission-header">
        <button 
          onClick={() => navigate('/curator/submissions')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Submissions
        </button>
        
        <h1>Create a New Submission</h1>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="edit-form">
        <div className="form-section">
          <h2>Basic Information</h2>
          
          {/* Gallery & Collection Selection - Hidden if pre-filled from URL */}
          {!prefilledCollection ? (
            <div className="form-row">
              <div className="form-group">
                <label>Gallery *</label>
                <select
                  value={formData.gallery_id}
                  onChange={(e) => handleInputChange('gallery_id', e.target.value)}
                  required
                >
                  <option value="">Select Gallery</option>
                  {userGalleries.map(gallery => (
                    <option key={gallery.gallery_id} value={gallery.gallery_id}>
                      {gallery.name} {gallery.address ? `- ${gallery.address}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Collection *</label>
                <select
                  value={formData.collection_id}
                  onChange={(e) => handleInputChange('collection_id', e.target.value)}
                  required
                >
                  <option value="">Select Collection</option>
                  {collections.map(collection => (
                    <option key={collection.collection_id} value={collection.collection_id}>
                      {collection.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            /* Display prefilled gallery and collection as read-only info */
            <div className="form-row">
              <div className="form-group">
                <label>Gallery</label>
                <div className="prefilled-value">
                  <Building size={16} />
                  <span>{prefilledGallery?.name}</span>
                </div>
              </div>
              
              <div className="form-group">
                <label>Collection</label>
                <div className="prefilled-value">
                  <Grid3X3 size={16} />
                  <span>{prefilledCollection?.title}</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Submission title"
              required
            />
          </div>

          <div className="form-group">
            <label>Cover Image</label>
            <div className="cover-image-row">
              {formData.cover_image && (
                <div className="cover-image-container">
                  <img src={formData.cover_image} alt="Cover" className="cover-preview-small" />
                </div>
              )}
              <div className="cover-upload-controls">
                <label className="upload-btn">
                  <Upload size={16} /> Upload New
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageUpload}
                    disabled={uploadingImage}
                    style={{ display: 'none' }}
                  />
                </label>
                {uploadingImage && <span className="uploading-text">Uploading...</span>}
              </div>
            </div>
            <small>Will use collection cover image if not provided</small>
          </div>
        </div>

        <div className="form-section">
          <h2>Submission Settings</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Open Date (Optional)</label>
              <input
                type="datetime-local"
                value={formatDateForInput(formData.open_date)}
                onChange={(e) => handleDateChange('open_date', e.target.value)}
              />
              <small className="field-note">When this submission becomes visible to the public. Leave empty to make it visible immediately.</small>
            </div>

            <div className="form-group">
              <label>Submission Deadline *</label>
              <input
                type="datetime-local"
                value={formatDateForInput(formData.submission_deadline)}
                onChange={(e) => handleDateChange('submission_deadline', e.target.value)}
                required
              />
              <small className="field-note">Defaults to 11:59 PM if no time is specified</small>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Max Submissions per Artist</label>
              <input
                type="number"
                min="1"
                value={formData.max_allowed}
                onChange={(e) => handleInputChange('max_allowed', e.target.value)}
              />
            </div>
            
            <div className="form-group">
              <label>Submission Price ($)</label>
              <div className="price-input-wrapper">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.submission_price}
                  onChange={(e) => handleInputChange('submission_price', e.target.value)}
                />
                {formData.member_free && formData.submission_price > 0 && (
                  <span className="price-waived-badge">Free for Members</span>
                )}
              </div>
            </div>
          </div>

          <div className="member-fee-waiver-option">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={formData.member_free}
                onChange={(e) => handleInputChange('member_free', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
            <div className="toggle-content">
              <span className="toggle-label">Free for {organization?.name || 'Organization'} Members</span>
              <span className="toggle-description">Automatically waive submission fee for paid members of {organization?.name || 'your organization'}</span>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <h2 style={{ margin: 0 }}>Submission Details</h2>
              <p className="section-description" style={{ margin: '4px 0 0 0' }}>Configure the fields shown on the public submission page. These come from your organization template.</p>
            </div>
            {organizationTemplate && (
              <button 
                type="button"
                onClick={syncWithTemplate}
                className="btn-secondary"
                style={{ marginLeft: '16px', whiteSpace: 'nowrap' }}
                title="Sync with latest template"
              >
                <RefreshCw size={16} />
                Sync with Template
              </button>
            )}
          </div>
          
          <div className="form-sections-container">
            {!Array.isArray(formSections) || formSections.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px', border: '2px dashed #e5e7eb' }}>
                <AlertCircle size={32} color="#9ca3af" style={{ margin: '0 auto 12px' }} />
                <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                  {!organization ? 
                    'Select a collection with an organization to load template fields' : 
                    !organizationTemplate ?
                    'No template found for this organization. Use the template manager to create one.' :
                    'No fields defined in template. Use the template manager to add fields.'}
                </p>
              </div>
            ) : formSections.map((section, index) => (
              <div key={section.id || index} className="form-section-item" style={{
                opacity: section.enabled === false ? 0.6 : 1,
                backgroundColor: section.enabled === false ? '#f9fafb' : 'white'
              }}>
                {/* Field Title - Editable */}
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <input
                    type="text"
                    value={section.label || ''}
                    onChange={(e) => updateFormSection(index, 'label', e.target.value)}
                    placeholder="Field Title"
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      border: 'none',
                      borderBottom: '2px solid #e5e7eb',
                      padding: '8px 0',
                      width: '100%',
                      backgroundColor: 'transparent'
                    }}
                  />
                </div>

                {/* Field Controls Row */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  flexWrap: 'wrap'
                }}>
                  {/* Enabled Toggle */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={section.enabled !== false}
                      onChange={(e) => updateFormSection(index, 'enabled', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Visible</span>
                  </label>

                  {/* Required Toggle */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={section.required || false}
                      onChange={(e) => updateFormSection(index, 'required', e.target.checked)}
                      disabled={section.enabled === false}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: '500', color: section.enabled === false ? '#9ca3af' : 'inherit' }}>Required</span>
                  </label>

                  {/* Field Type Selector */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Type:</span>
                    <select
                      value={section.type || 'text'}
                      onChange={(e) => updateFormSection(index, 'type', e.target.value)}
                      disabled={section.enabled === false}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb',
                        fontSize: '14px',
                        backgroundColor: section.enabled === false ? '#f3f4f6' : 'white'
                      }}
                    >
                      <option value="text">Text Input</option>
                      <option value="textarea">Text Area</option>
                      <option value="html">Rich Text/HTML</option>
                      <option value="date">Date</option>
                      <option value="datetime">Date & Time</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="number">Number</option>
                      <option value="select">Dropdown</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="radio">Radio Buttons</option>
                    </select>
                  </div>
                </div>

                {/* Only show help text if available */}
                {section.helpText && (
                  <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280', fontStyle: 'italic' }}>
                    {section.helpText}
                  </p>
                )}
                
                {/* Date field */}
                {section.type === 'date' && (
                  <div className="form-group">
                    <input
                      type="date"
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                    />
                  </div>
                )}
                
                {/* Datetime field */}
                {section.type === 'datetime' && (
                  <div className="form-group">
                    <input
                      type="datetime-local"
                      value={section.value ? formatDateForInput(section.value) : ''}
                      onChange={(e) => {
                        // Convert to ISO 8601 format with timezone
                        const localDate = new Date(e.target.value);
                        const isoString = localDate.toISOString();
                        updateFormSection(index, 'value', isoString);
                      }}
                    />
                  </div>
                )}
                
                {/* HTML/Rich text field */}
                {section.type === 'html' && (
                  <div className="form-group">
                    <RichTextEditor
                      value={section.value || ''}
                      onChange={(value) => updateFormSection(index, 'value', value)}
                      placeholder={section.placeholder || 'Enter rich text content...'}
                      minHeight="150px"
                      enableImageUpload={true}
                      imageFolder="submission_content"
                    />
                  </div>
                )}
                
                {/* Text field */}
                {section.type === 'text' && (
                  <div className="form-group">
                    <input
                      type="text"
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                      placeholder={section.placeholder || ''}
                    />
                  </div>
                )}
                
                {/* Textarea field */}
                {section.type === 'textarea' && (
                  <div className="form-group">
                    <textarea
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                      placeholder={section.placeholder || ''}
                      rows="4"
                    />
                  </div>
                )}
                
                {/* Email field */}
                {section.type === 'email' && (
                  <div className="form-group">
                    <input
                      type="email"
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                      placeholder={section.placeholder || ''}
                    />
                  </div>
                )}
                
                {/* Phone field */}
                {section.type === 'phone' && (
                  <div className="form-group">
                    <input
                      type="tel"
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                      placeholder={section.placeholder || ''}
                    />
                  </div>
                )}
                
                {/* Number field */}
                {section.type === 'number' && (
                  <div className="form-group">
                    <input
                      type="number"
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                      placeholder={section.placeholder || ''}
                    />
                  </div>
                )}
                
                {/* Select field */}
                {section.type === 'select' && (
                  <div className="form-group">
                    <select
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                    >
                      <option value="">Select an option...</option>
                      {section.options?.map((option, i) => (
                        <option key={i} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Radio field */}
                {section.type === 'radio' && (
                  <div className="form-group">
                    {section.options?.map((option, i) => (
                      <label key={i} style={{ display: 'block', marginBottom: '8px' }}>
                        <input
                          type="radio"
                          name={`radio-${section.id}`}
                          value={option}
                          checked={section.value === option}
                          onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                          style={{ marginRight: '8px' }}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                )}
                
                {/* Checkbox field */}
                {section.type === 'checkbox' && (
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={section.value === true || section.value === 'true'}
                        onChange={(e) => updateFormSection(index, 'value', e.target.checked)}
                        style={{ marginRight: '8px' }}
                      />
                      {section.helpText || 'Check to enable'}
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <p style={{ margin: 0, fontStyle: 'italic', color: '#666' }}>
              To add or remove fields, use the template manager.
            </p>
            <button 
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/curator/submission-template')}
              style={{ marginLeft: '16px' }}
            >
              <Settings size={16} />
              Manage Template
            </button>
          </div>
        </div>

        <div className="form-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2>Configuration (Advanced)</h2>
            <button
              type="button"
              onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
              className="toggle-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: '#f0f0f0',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {showAdvancedConfig ? 'Hide' : 'Show'} Configuration
              {showAdvancedConfig ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>
          
          {showAdvancedConfig && (
            <>
              <div className="form-group">
                <label>Acceptance Info (JSON)</label>
                <textarea
                  value={formData.acceptance_info}
                  onChange={(e) => handleInputChange('acceptance_info', e.target.value)}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.max(e.target.scrollHeight, 120) + 'px';
                  }}
                  placeholder="JSON object with acceptance details"
                  className="json-input"
                  style={{ resize: 'none' }}
                />
                <small>Raw JSON for show day logistics sent to accepted artists</small>
              </div>

              <div className="form-group">
                <label>Form Config (JSON)</label>
                <textarea
                  value={formData.form_config}
                  onChange={(e) => handleInputChange('form_config', e.target.value)}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.max(e.target.scrollHeight, 120) + 'px';
                  }}
                  placeholder="JSON object defining form structure"
                  className="json-input"
                  style={{ resize: 'none' }}
                />
                <small>Configure which form fields are shown to artists during submission</small>
              </div>
            </>
          )}
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            onClick={() => navigate('/curator/submissions')}
            className="cancel-button"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={saving}
            className="save-button"
          >
            {saving ? (
              <>
                <div className="spinner" />
                Creating...
              </>
            ) : (
              <>
                <Save size={16} />
                Create Submission
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewSubmission;